import { User } from "../../lib/appManagers/appUsersManager";
import rootScope from "../../lib/rootScope";
import request from "../utils/request";
import { clearMetaCache } from "./index";

/**
 * 工作号信息。
 *
 * **绑定不再由本页负责。** 原先这里还有 `checkBind` 与 `bindGroup` 两个函数，
 * 页面自己调 `/auth/check-group-binding` 判断绑定、调 `/auth/bind-group` 建立绑定、
 * 并把 `groupId`/`accessToken` 等写进 sessionStorage。
 *
 * 迁移后这两件事都归 Dmex 客户端：
 *  · 是否已绑定由客户端查（它掌握登录态与账号列表），未绑定则由客户端弹「加入工作组」，
 *    并在启动前拦截——一套 UI 覆盖所有平台，不必每个 web 应用各写一遍；
 *  · 页面手里不再持有任何 token，业务请求也改由客户端代发（见 utils/bridgeRequest.ts）。
 *
 * 删掉的不只是两个函数，还有随之而来的问题：页面负责「重新绑定」在结构上就说不通
 * ——它既不知道 dmex 的登录态，也没有触发全局登出的能力。
 */

/**
 * 修改工作号信息（头像、昵称等）。仍由客户端代发，页面不持凭据。
 *
 * `force` 用于「绑定刚到达」这一刻补发（见 utils/pcApi.js 的 `setTolinkBinding`）：
 * 登录事件触发的那几次通常发生在绑定之前，会被客户端按「未绑定」拦下
 * （见 utils/bridgeRequest.ts），而它们又都落在下面的 1 秒合并窗口里 ——
 * 不强制补发的话，头像和昵称就再也没有机会同步上去了。
 */
export async function modifyUserInfo(force = false) {
  // 合并突发调用：`avatar_update` / `peer_title_edit` / `user_auth` 三个事件都会调这里，
  // 一次头像更新往往会连着触发好几个，于是短时间内重复上传头像并打多次接口。
  // 采用「首个立即执行、随后 1 秒内忽略」：既保留及时性，又不丢第一批变更
  // （被忽略的那些本就是在同一批状态变化里产生的，首个请求读到的已是当时最新状态）。
  if (!force && !modifyAllowed()) {
    console.log('Tolink modify user info skipped: 距上次调用过近');
    return;
  }
  lastModifyAt = Date.now();

  const tgId = rootScope.myId;
  const tgUser = await rootScope.managers.appUsersManager.getUser(tgId);

  const userParams = await buildParams(tgUser);
  console.log("Tolink modify user info with params:", userParams);
  const res = await request({
    url: "/group-member/modify",
    method: "post",
    data: {
      ...userParams,
    },
  });

  // 备注等信息可能刚被改过，让会话标题的下一次查询拿到新值。
  // 不清缓存的话，改完备注要等 TTL 才在列表里生效。
  clearMetaCache();
  return res;
}

let lastModifyAt = 0
const MODIFY_MIN_GAP_MS = 1000

function modifyAllowed(): boolean {
  return Date.now() - lastModifyAt >= MODIFY_MIN_GAP_MS
}

// 转换 user
async function buildParams(user: User) {
  // 头像地址由客户端下发（见 utils/pcApi.js 的 `setTolinkAvatarUrl`）。
  //
  // 原先这里调 `uploadAvatarToAws` 自己上传，那条路已经不通了：静态 AWS key/secret
  // 不再下发（客户端 store 里的 `aws` 只剩 region），且服务端把旧的 `get_s3_token`
  // 停用了（回 `2008001 upload service is disabled`）。新的直传接口要鉴权，
  // 凭据只有客户端有，所以上传归客户端，页面只取结果。
  //
  // 拿不到就留空：`sysAvatar` 是选填的，留空比传一个错值好。
  const sysAvatar = sessionStorage.getItem('tolink_avatarUrl') || '';

  return {
    sysId: user.id,
    sysAccount: user.username,
    sysNick: user.sortName,
    sysPhone: user.phone,
    sysCountry: user.lang_code,
    sysAvatar: sysAvatar,
  };
}
